/**
 * Helpers for tiered Stripe Prices used by the agreements flow.
 *
 * The picker exposes Stripe-shaped tiers (`up_to`, `unit_amount`, `flat_amount`)
 * in **minor units** (pence/cents). The public review payload uses camelCase
 * (`upTo`, `unitAmount`, `flatAmount`) in **major units** (per the
 * `/public/agreements/:token` contract). These helpers normalise both shapes
 * so the FE can preview the matched band on the admin form and display the
 * already-matched band on the customer accept page without divergent code.
 */

const TIER_KEYS = {
  upTo: ['upTo', 'up_to'],
  unitAmount: ['unitAmount', 'unit_amount'],
  flatAmount: ['flatAmount', 'flat_amount'],
};

const pickKey = (obj, keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
};

/**
 * Normalise a tier-like object so callers can read `upTo / unitAmount / flatAmount`
 * regardless of whether the source is the Stripe picker (snake_case, pence) or
 * the public review payload (camelCase, major units).
 *
 * The returned object preserves the *original* numeric magnitudes — we do NOT
 * convert pence ↔ major here; that's the caller's responsibility based on the
 * source shape (because converting a ÷100 of a public-payload number would
 * silently corrupt amounts that already arrive in major units).
 */
const readTier = (tier) => {
  if (!tier) return null;
  const upTo = pickKey(tier, TIER_KEYS.upTo);
  return {
    upTo: upTo === 'inf' || upTo === null || upTo === undefined ? Infinity : Number(upTo),
    upToRaw: upTo,
    unitAmount: pickKey(tier, TIER_KEYS.unitAmount) ?? null,
    flatAmount: pickKey(tier, TIER_KEYS.flatAmount) ?? null,
  };
};

/**
 * Find the tier into which a banding quantity falls.
 *
 * Volume-tier semantics (which is what we use for revenue / FTE bands):
 * the customer's quantity falls into exactly one tier, and the band's
 * `flat_amount` is the price.
 *
 * @param {Array<object>} tiers — tier objects in either Stripe or contract shape.
 * @param {number|string} bandingQuantity
 * @returns {{ index: number, upTo: number, upToRaw: any, unitAmount: number|null, flatAmount: number|null } | null}
 */
const findMatchingTier = (tiers, bandingQuantity) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const q = Number(bandingQuantity);
  if (!Number.isFinite(q) || q <= 0) return null;

  let prevUpTo = 0;
  for (let i = 0; i < tiers.length; i++) {
    const t = readTier(tiers[i]);
    if (!t) continue;
    if (q > prevUpTo && q <= t.upTo) {
      return { index: i, ...t };
    }
    prevUpTo = t.upTo;
  }
  // Falls beyond the last finite tier — return the last one (which should be `inf`).
  const last = readTier(tiers[tiers.length - 1]);
  return last ? { index: tiers.length - 1, ...last } : null;
};

/**
 * Resolve the banding unit + label from a Stripe Price / Product pair.
 *
 * Per the agreements API contract, Price metadata wins over Product metadata
 * (Prices can override the inherited unit, e.g. a flat sub Price on a banded
 * product). When neither carries `cyf_billing_unit_label` we fall back to a
 * sensible default derived from `cyf_billing_unit`.
 *
 * @param {object} [price]
 * @param {object} [product]
 * @returns {{ unit: ('revenue'|'fte'|'flat'|null), label: (string|null) }}
 */
const resolveBillingUnit = (price, product) => {
  const pMeta = price?.metadata || {};
  const prMeta = product?.metadata || {};
  const unit = pMeta.cyf_billing_unit || prMeta.cyf_billing_unit || null;
  const label =
    pMeta.cyf_billing_unit_label ||
    prMeta.cyf_billing_unit_label ||
    (unit === 'revenue' ? 'Annual revenue' : unit === 'fte' ? 'Number of FTEs' : null);
  return { unit, label };
};

/**
 * `true` when the picker option's Price uses tiered pricing (so the line item
 * needs a `bandingQuantity` and we hide the manual unit-amount input).
 */
const isTieredPrice = (price) =>
  !!(price && (price.billing_scheme === 'tiered' || price.billingScheme === 'tiered'));

/**
 * Format a major-unit money amount with a currency symbol.
 *
 * `null` / `undefined` are rendered as `—` so callers can pass tier amounts
 * straight through without first checking for missing values (volume tiers
 * may omit `unit_amount` when only `flat_amount` is set, and vice versa).
 */
const formatMajorMoney = (currencyIso, majorAmount, symbolFn) => {
  if (majorAmount === null || majorAmount === undefined) return '—';
  const n = Number(majorAmount);
  if (!Number.isFinite(n)) return '—';
  const sym = symbolFn ? symbolFn(currencyIso) : '';
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export { readTier, findMatchingTier, resolveBillingUnit, isTieredPrice, formatMajorMoney };
